// cspell:words ansicpg colortbl emdash emspace endash enspace filetbl fldinst fonttbl footerf footerl footerr headerf
// cspell:words headerl headerr latentstyles ldblquote listoverridetable listtable lquote nestcell nestrow objdata
// cspell:words pgdsctbl qmspace rdblquote revtbl rquote rsidtbl themedata xmlnstbl

// Groups whose content is formatting data, metadata or embedded binaries rather than document text.
const SKIPPED_DESTINATIONS = new Set([
  'colortbl',
  'datastore',
  'filetbl',
  'fldinst',
  'fonttbl',
  'footer',
  'footerf',
  'footerl',
  'footerr',
  'generator',
  'header',
  'headerf',
  'headerl',
  'headerr',
  'info',
  'latentstyles',
  'listoverridetable',
  'listtable',
  'object',
  'objdata',
  'pgdsctbl',
  'pict',
  'revtbl',
  'rsidtbl',
  'stylesheet',
  'tc',
  'themedata',
  'xe',
  'xmlnstbl',
]);

const CONTROL_WORD_TEXT: Record<string, string> = {
  bullet: '•',
  cell: '\t',
  emdash: '—',
  emspace: ' ',
  endash: '–',
  enspace: ' ',
  ldblquote: '“',
  line: '\n',
  lquote: '‘',
  nestcell: '\t',
  nestrow: '\n',
  page: '\n\n',
  par: '\n',
  qmspace: ' ',
  rdblquote: '”',
  row: '\n',
  rquote: '’',
  sect: '\n\n',
  tab: '\t',
};

const CONTROL_SYMBOL_TEXT: Record<string, string> = {
  '\\': '\\',
  '{': '{',
  '}': '}',
  '~': ' ', // non-breaking space
  _: '‑', // non-breaking hyphen
  '-': '',
  '\n': '\n',
  '\r': '\n',
};

const CODEPAGE_ENCODINGS: Record<number, string> = {
  932: 'shift_jis',
  936: 'gbk',
  949: 'euc-kr',
  950: 'big5',
  10000: 'macintosh',
  65001: 'utf-8',
};

const CONTROL_WORD = /\\([a-zA-Z]{1,32})(-?\d{1,10})? ?/y;

function createDecoder(codepage: number): TextDecoder {
  try {
    return new TextDecoder(CODEPAGE_ENCODINGS[codepage] ?? `windows-${codepage}`);
  } catch {
    return new TextDecoder('windows-1252');
  }
}

type GroupState = { skip: boolean; unicodeFallbackLength: number };

/** Plain text of an RTF document: paragraphs become line breaks, table cells become tabs, formatting is dropped. */
export function rtfToText(rtf: string): string {
  let state: GroupState = { skip: false, unicodeFallbackLength: 1 };
  const stack: GroupState[] = [];
  let decoder = createDecoder(1252);
  let text = '';
  // `\'hh` bytes are collected so multi-byte code pages decode as whole characters.
  let bytes: number[] = [];
  // ANSI fallback characters still to drop after a `\uN`.
  let fallbackToSkip = 0;

  const flushBytes = () => {
    if (bytes.length === 0) return;
    text += decoder.decode(new Uint8Array(bytes));
    bytes = [];
  };

  const emit = (value: string) => {
    flushBytes();
    if (!state.skip) text += value;
  };

  let i = 0;
  while (i < rtf.length) {
    const char = rtf[i]!;

    if (char === '{') {
      flushBytes();
      stack.push(state);
      state = { ...state };
      fallbackToSkip = 0;
      i++;
      continue;
    }

    if (char === '}') {
      flushBytes();
      state = stack.pop() ?? state;
      fallbackToSkip = 0;
      i++;
      continue;
    }

    if (char === '\r' || char === '\n') {
      i++;
      continue;
    }

    if (char !== '\\') {
      if (fallbackToSkip > 0) fallbackToSkip--;
      else emit(char);
      i++;
      continue;
    }

    const next = rtf[i + 1] ?? '';

    if (next === "'") {
      const byte = Number.parseInt(rtf.slice(i + 2, i + 4), 16);
      i += 4;
      if (Number.isNaN(byte)) continue;
      if (fallbackToSkip > 0) fallbackToSkip--;
      else if (!state.skip) bytes.push(byte);
      continue;
    }

    if (next === '*') {
      state.skip = true;
      i += 2;
      continue;
    }

    CONTROL_WORD.lastIndex = i;
    const match = CONTROL_WORD.exec(rtf);
    if (!match) {
      const symbolText = CONTROL_SYMBOL_TEXT[next];
      if (symbolText !== undefined) {
        if (fallbackToSkip > 0 && symbolText) fallbackToSkip--;
        else emit(symbolText);
      }
      i += 2;
      continue;
    }

    i = CONTROL_WORD.lastIndex;
    const word = match[1]!;
    const param = match[2] === undefined ? null : Number.parseInt(match[2], 10);

    if (SKIPPED_DESTINATIONS.has(word)) {
      state.skip = true;
    } else if (word === 'bin') {
      // Raw binary data follows; the control word's trailing space was already consumed.
      i += param ?? 0;
    } else if (word === 'ansicpg' && param !== null) {
      flushBytes();
      decoder = createDecoder(param);
    } else if (word === 'uc' && param !== null) {
      state.unicodeFallbackLength = param;
    } else if (word === 'u' && param !== null) {
      // Signed 16-bit code units; surrogate pairs come as two `\u` words and join up in the string.
      emit(String.fromCharCode(param < 0 ? param + 65536 : param));
      fallbackToSkip = state.unicodeFallbackLength;
    } else if (word in CONTROL_WORD_TEXT) {
      emit(CONTROL_WORD_TEXT[word]!);
    }
  }
  flushBytes();

  return text
    .replace(/[^\S\n]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
